import { Badge } from '@thefarmersfront/kitchen';

import type { ColDef, ICellRendererParams, IRowNode, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';

import { TextButtonCell } from '~/components/common';

import { maskName } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { DATE_FORMAT, DELIVERY_AGENCY_SHIPPING_LABEL_STATUS_BADGE_COLOR } from '~/constants';

import type { TypeDeliveryAgencyShippingLabel } from '~/types/delivery-agency.types';

const getDeliveryAgencyTrackingsColumnDefs = ({
  currentPage,
  pageSize,
  onClickViewDetail,
}: {
  currentPage: number;
  pageSize: number;
  onClickViewDetail: (order: TypeDeliveryAgencyShippingLabel) => void;
}) => {
  const columnDefs: ColDef<TypeDeliveryAgencyShippingLabel>[] = [
    {
      headerName: 'No',
      valueGetter: ({ node }) => (node?.rowIndex ?? 0) + 1 + currentPage * pageSize,
      width: 96,
      headerCheckboxSelection: true,
      checkboxSelection: true,
    },
    {
      field: 'shippingLabelStatus',
      headerName: '운송장 상태',
      cellDataType: false,
      width: 105,
      cellRenderer: Badge,
      cellRendererParams: ({
        value,
      }: ICellRendererParams<
        TypeDeliveryAgencyShippingLabel,
        TypeDeliveryAgencyShippingLabel['shippingLabelStatus']
      >) => ({
        variant: 'light',
        status: true,
        color: value ? DELIVERY_AGENCY_SHIPPING_LABEL_STATUS_BADGE_COLOR[value.code] : undefined,
        children: value?.description || '-',
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'statusUpdatedAt',
      headerName: '상태 마지막 업데이트 일시',
      width: 170,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeDeliveryAgencyShippingLabel, TypeDeliveryAgencyShippingLabel['statusUpdatedAt']>) =>
        formatDate(value, DATE_FORMAT.DATE_TIME) ?? '-',
    },
    {
      field: 'shippingLabelNumber',
      headerName: '운송장 번호',
      width: 245,
      cellRenderer: TextButtonCell,
      cellRendererParams: ({
        data,
      }: ICellRendererParams<
        TypeDeliveryAgencyShippingLabel,
        TypeDeliveryAgencyShippingLabel['shippingLabelNumber']
      >) => ({
        children: data?.shippingLabelNumber,
        onClick: ({ data }: IRowNode<TypeDeliveryAgencyShippingLabel>) => {
          if (!data) return;
          onClickViewDetail(data);
        },
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'isPrinted',
      headerName: '운송장 출력',
      width: 113,
      valueGetter: ({
        data,
      }: ValueGetterParams<TypeDeliveryAgencyShippingLabel, TypeDeliveryAgencyShippingLabel['isPrinted']>) => {
        if (!data) return '-';
        return data.isPrinted ? '출력함' : '출력안함';
      },
    },
    {
      field: 'outboundCode',
      headerName: '출고번호',
      width: 160,
    },
    {
      field: 'clientOrderCodes',
      headerName: '판매처 주문번호',
      width: 160,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeDeliveryAgencyShippingLabel, TypeDeliveryAgencyShippingLabel['clientOrderCodes']>) =>
        value?.length ? value.join(', ') : '-',
    },
    {
      field: 'estimatedDeliveryDate',
      headerName: '배송 예정일',
      width: 100,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<
        TypeDeliveryAgencyShippingLabel,
        TypeDeliveryAgencyShippingLabel['estimatedDeliveryDate']
      >) => formatDate(value, DATE_FORMAT.DATE) ?? '-',
    },
    {
      field: 'deliveryType.description',
      headerName: '배송 유형',
      width: 80,
    },
    {
      field: 'courierCode.description',
      headerName: '배송사',
      width: 88,
    },
    {
      field: 'receiverName',
      headerName: '수령자명',
      width: 100,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeDeliveryAgencyShippingLabel, TypeDeliveryAgencyShippingLabel['receiverName']>) =>
        maskName(value) ?? '-',
    },
    {
      field: 'ordererName',
      headerName: '주문자명',
      width: 100,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeDeliveryAgencyShippingLabel, TypeDeliveryAgencyShippingLabel['ordererName']>) =>
        maskName(value) ?? '-',
    },
    {
      field: 'productName',
      headerName: '상품명',
      flex: 1,
      minWidth: 252,
      width: 252,
    },
    {
      field: 'createdAt',
      headerName: '운송장 생성일시',
      width: 160,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeDeliveryAgencyShippingLabel, TypeDeliveryAgencyShippingLabel['createdAt']>) =>
        formatDate(value, DATE_FORMAT.DATE_TIME) ?? '-',
    },
  ];

  return { columnDefs };
};

export default getDeliveryAgencyTrackingsColumnDefs;
